import type {
  ResourceSortDescriptor,
  ResourceSortFieldDescriptor,
  ResourceSortInput
} from './types';

const SORT_DIRECTIONS = new Set<unknown>([1, -1, '1', '-1', 'asc', 'desc']);

/**
 * Normalize any {@link ResourceSortInput} form — `'name'`, `'-created_at'`,
 * `['status', '-created_at']`, a `{ key, direction }` or `{ field, direction }` descriptor, an ordered list of
 * descriptors, or an explicit `{ created_at: -1 }` map — into the ordered `$sort`
 * direction map the records API expects. In a list, the first occurrence of a key wins.
 * Shared by the resource handle's `search` and the reactive record hooks so every
 * read path builds the same body.
 */
export function normalizeSort(sort: ResourceSortInput): Record<string, string | number> {
  if (typeof sort === 'string') {
    const key = sort.startsWith('-') ? sort.slice(1) : sort;

    return key === '' ? {} : { [key]: sort.startsWith('-') ? -1 : 1 };
  }

  if (isSortDescriptor(sort)) {
    const key = descriptorKey(sort);

    return key === '' ? {} : { [key]: sort.direction === 'desc' ? -1 : 1 };
  }

  if (Array.isArray(sort)) {
    const normalized: Record<string, string | number> = {};

    for (const item of sort) {
      const [key, direction] =
        typeof item === 'string'
          ? [item.startsWith('-') ? item.slice(1) : item, item.startsWith('-') ? -1 : 1]
          : [descriptorKey(item), item.direction === 'desc' ? -1 : 1];

      if (key !== '' && !(key in normalized)) {
        normalized[key] = direction;
      }
    }

    return normalized;
  }

  for (const [key, direction] of Object.entries(sort)) {
    if (!SORT_DIRECTIONS.has(typeof direction === 'string' ? direction.toLowerCase() : direction)) {
      throw new Error(
        `Invalid sort direction for "${key}". Use 1, -1, "asc", or "desc", or a { key, direction } descriptor.`
      );
    }
  }

  return sort;
}

function descriptorKey(descriptor: ResourceSortDescriptor | ResourceSortFieldDescriptor): string {
  return 'key' in descriptor ? descriptor.key : descriptor.field;
}

/**
 * A direction map whose only entries happen to be fields named `key` (or `field`) and `direction`
 * is indistinguishable from the descriptor form, so the descriptor wins — direction
 * maps use numeric values in practice, making the collision theoretical.
 */
function isSortDescriptor(
  sort: object
): sort is ResourceSortDescriptor | ResourceSortFieldDescriptor {
  return (
    !Array.isArray(sort) &&
    Object.keys(sort).length === 2 &&
    'direction' in sort &&
    (('key' in sort && typeof sort.key === 'string') ||
      ('field' in sort && typeof sort.field === 'string')) &&
    (sort.direction === 'asc' || sort.direction === 'desc')
  );
}
